interface CapturedLog {
  level: 'log' | 'info' | 'debug' | 'warn' | 'error';
  message: string;
  timestamp: string;
}

const MAX_LOGS = 50;
const MAX_VALUE_LENGTH = 200;
const ELLIPSIS = '…';

const SECRET_KEY_PATTERN = /^(password|passwd|pwd|secret|token|api[_-]?key|auth|authorization|credential|cookie|bearer|session|private[_-]?key)$/i;

const logBuffer: CapturedLog[] = [];
let initialized = false;

function isSecretKey(key: string): boolean {
  return SECRET_KEY_PATTERN.test(key);
}

function redactSecrets(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(redactSecrets);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = isSecretKey(k) ? '[REDACTED]' : redactSecrets(v);
  }
  return out;
}

function stringifyWithLimit(value: unknown): string {
  let s: string;
  try {
    const sanitized = redactSecrets(value);
    s = typeof sanitized === 'string' ? sanitized : JSON.stringify(sanitized);
  } catch {
    if (typeof value === 'object' && value !== null) {
      s = '[Unserializable]';
    } else if (typeof value === 'symbol') {
      s = value.toString();
    } else {
      const prim = value as string | number | boolean | undefined | bigint;
      s = prim === undefined ? 'undefined' : String(prim);
    }
  }
  if (s.length > MAX_VALUE_LENGTH) {
    return s.slice(0, MAX_VALUE_LENGTH) + ELLIPSIS;
  }
  return s;
}

export function initConsoleCapture() {
  if (initialized) return;
  initialized = true;

  const originalLog = console.log;
  const originalInfo = console.info;
  const originalDebug = console.debug;
  const originalWarn = console.warn;
  const originalError = console.error;

  console.log = (...args: unknown[]) => {
    pushLog('log', args);
    originalLog.apply(console, args);
  };

  console.info = (...args: unknown[]) => {
    pushLog('info', args);
    originalInfo.apply(console, args);
  };

  console.debug = (...args: unknown[]) => {
    pushLog('debug', args);
    originalDebug.apply(console, args);
  };

  console.warn = (...args: unknown[]) => {
    pushLog('warn', args);
    originalWarn.apply(console, args);
  };

  console.error = (...args: unknown[]) => {
    pushLog('error', args);
    originalError.apply(console, args);
  };
}

function pushLog(level: CapturedLog['level'], args: unknown[]) {
  const message = args.map(stringifyWithLimit).join(' ');

  logBuffer.push({ level, message, timestamp: new Date().toISOString() });
  if (logBuffer.length > MAX_LOGS) {
    logBuffer.shift();
  }
}

export function getRecentLogs(): CapturedLog[] {
  return [...logBuffer];
}
